import { ArrowRight, Check, Copy, Crosshair, ShieldCheck, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Container } from '../components/Container';
import { PlayersCard } from '../components/PlayersCard';
import { SectionHeading } from '../components/SectionHeading';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { StatPill } from '../components/StatPill';
import { WorldMapCard } from '../components/WorldMapCard';
import { useServerTelemetry } from '../hooks/useServerTelemetry';

const useClipboard = value => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  return { copied, copy };
};

const FeatureCard = ({ title, description, Icon }) => (
  <div className="rounded-3xl border border-white/10 bg-base-200/20 p-6">
    <div className="flex items-center gap-3">
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/15 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-lg font-semibold">{title}</div>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-base-content/70">{description}</p>
  </div>
);

export const KweebecWorldPage = () => {
  const telemetry = useServerTelemetry();
  const { copied, copy } = useClipboard(telemetry.address);

  const features = useMemo(
    () => [
      {
        title: 'Guns + raids',
        description: 'Firearms, base raids, and high-stakes fights for resources. Every wipe is a fresh story.',
        icon: Crosshair
      },
      {
        title: 'Custom crafts',
        description: 'Progression-focused crafting with custom recipes, economy sinks, and meaningful upgrades.',
        icon: Wrench
      },
      {
        title: 'Built-in anti-cheat',
        description: 'First-class anti-cheat designed for the server from day one, with fast detection and fair enforcement.',
        icon: ShieldCheck
      }
    ],
    []
  );

  return (
    <div id="top" className="min-h-screen bg-base-100 text-base-content">
      <SiteHeader address={telemetry.address} />

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(88,214,141,0.16),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(109,179,255,0.12),transparent_55%)]" />
          <div className="kw-blob kw-blob--slow absolute -left-24 top-20 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
          <div className="kw-blob kw-blob--fast kw-blob--delay absolute -right-24 top-44 h-72 w-72 rounded-full bg-secondary/15 blur-3xl" />

          <Container className="relative py-16 sm:py-20">
            <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
              <div className="grid gap-8 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="badge badge-outline border-white/10 bg-base-200/20 text-xs">{telemetry.region}</div>
                  <div className="badge badge-outline border-white/10 bg-base-200/20 text-xs">{telemetry.version}</div>
                  <div className={['badge text-xs', telemetry.status === 'online' ? 'badge-success' : 'badge-error'].join(' ')}>
                    {telemetry.status === 'online' ? 'Online' : 'Offline'}
                  </div>
                </div>

                <h1 className="text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                  Kweebec <span className="text-primary">World</span>
                </h1>
                <p className="max-w-xl text-base leading-relaxed text-base-content/75">
                  A hardcore survival server built around firearms, raids, and relentless progression. Build smart, craft
                  better, and defend what you own.
                </p>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a href="#join" className="btn btn-primary">
                    Start playing
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <button type="button" className="btn btn-ghost border border-white/10" onClick={copy}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied' : telemetry.address}
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <StatPill label="Players" value={`${telemetry.playersOnline}/${telemetry.maxPlayers}`} />
                  <StatPill label="Ping" value={`${telemetry.pingMs}ms`} />
                  <StatPill label="Queue" value={telemetry.queue} />
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[2rem] border border-white/10 bg-base-200/20 p-6 shadow-glow">
                  <div className="grid gap-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">Session briefing</div>
                      <div className="badge badge-outline border-white/10 text-xs">Live</div>
                    </div>

                    <div className="grid gap-3">
                      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-base-300/10 px-4 py-3">
                        <div className="text-xs text-base-content/65">Wipe cycle</div>
                        <div className="text-sm font-semibold">Seasonal</div>
                      </div>
                      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-base-300/10 px-4 py-3">
                        <div className="text-xs text-base-content/65">Raiding</div>
                        <div className="text-sm font-semibold">Enabled</div>
                      </div>
                      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-base-300/10 px-4 py-3">
                        <div className="text-xs text-base-content/65">Economy</div>
                        <div className="text-sm font-semibold">Player-driven</div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-base-100/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Crosshair className="h-4 w-4 text-primary" />
                        Getting started
                      </div>
                      <div className="mt-2 grid gap-2 text-sm text-base-content/70">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          Place a bed to claim a chunk.
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-secondary" />
                          Control resources to win fights.
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                          Claims can be broken by explosions that destroy the bed.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section id="players" className="py-16">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="grid gap-4 lg:col-span-4">
                <SectionHeading
                  eyebrow="Community"
                  title="Who’s playing right now"
                  description="Live player names fetched from the server."
                />
                <div className="rounded-3xl border border-white/10 bg-base-200/20 p-6">
                  <div className="text-sm font-semibold">What to expect</div>
                  <div className="mt-3 grid gap-2 text-sm text-base-content/70">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      No pay-to-win.
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-secondary" />
                      Raids and PvP are part of the loop.
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                      Cheaters get removed fast.
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8">
                <PlayersCard players={telemetry.players} />
              </div>
            </div>
          </Container>
        </section>

        <section id="world" className="py-16">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-7">
                <WorldMapCard playersOnline={telemetry.playersOnline} pingMs={telemetry.pingMs} />
              </div>

              <div className="grid gap-6 lg:col-span-5">
                <SectionHeading
                  eyebrow="Explore"
                  title="A world built for discovery"
                  description="Biomes, routes, and points of interest designed for long-term survival play."
                />

                <div className="grid gap-4">
                  {features.map(feature => (
                    <FeatureCard key={feature.title} title={feature.title} description={feature.description} Icon={feature.icon} />
                  ))}
                </div>

                <div id="rules" className="rounded-3xl border border-white/10 bg-base-200/20 p-6">
                  <div className="text-sm font-semibold">Server rules</div>
                  <div className="mt-3 grid gap-2 text-sm text-base-content/70">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      No rules. Survive.
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-secondary" />
                      Use what you find.
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                      Don’t cheat.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section id="faq" className="py-16">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="FAQ"
                  title="Everything our players ask"
                  description="Quick answers to the important stuff before you join."
                />
              </div>

              <div className="grid gap-3 lg:col-span-7">
                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">How do I join?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    Add the server address, launch the correct version, and you’re in. Use the copy button in the hero.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">Are raids always enabled?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    Yes. Your protection is bed-based chunk claiming: if you place a bed, the chunk is claimed so other
                    players can’t place or break blocks there. Raiders can still use explosions to destroy the bed and
                    unclaim the chunk.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">Do you wipe?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    We do seasonal wipes to keep progression competitive. Wipe timing is announced in Discord.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">Is there a whitelist?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    Not right now. If you can connect, you can play.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">What are the rules?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    There are no gameplay rules: PvP, scams, and raids are allowed. The only hard rule is no cheating or
                    abusing exploits.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">Can I see the live map?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    Yes. Scroll to the world section to view the live map.
                  </div>
                </div>

                <div className="collapse collapse-arrow rounded-3xl border border-white/10 bg-base-200/20">
                  <input type="checkbox" />
                  <div className="collapse-title flex items-center text-sm font-semibold">How do you handle cheating?</div>
                  <div className="collapse-content pb-0 text-sm text-base-content/70">
                    The server uses an in-house anti-cheat plus manual review. Suspicious accounts get flagged quickly and
                    bans are enforced without warnings.
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section id="join" className="py-16">
          <Container>
            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-base-200/20 p-10 shadow-glow">
              <div className="kw-blob kw-blob--fast absolute -left-20 top-8 h-60 w-60 rounded-full bg-primary/20 blur-3xl" />
              <div className="kw-blob kw-blob--slow kw-blob--delay absolute -right-24 bottom-0 h-60 w-60 rounded-full bg-secondary/15 blur-3xl" />

              <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
                <div className="grid gap-4 lg:col-span-7">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary/80">Ready to play</div>
                  <div className="text-3xl font-semibold leading-tight sm:text-4xl">Join the world in under a minute</div>
                  <div className="text-sm leading-relaxed text-base-content/70">
                    Copy the server address, launch your client, and spawn into a no-rules survival world. Craft, loot, and
                    build fast.
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    <div className="rounded-3xl border border-white/10 bg-base-100/50 p-4">
                      <div className="text-sm font-semibold">1. Copy IP</div>
                      <div className="mt-1 text-xs text-base-content/65">{telemetry.address}</div>
                    </div>
                    <div className="rounded-3xl border border-white/10 bg-base-100/50 p-4">
                      <div className="text-sm font-semibold">2. Launch</div>
                      <div className="mt-1 text-xs text-base-content/65">Version {telemetry.version}</div>
                    </div>
                    <div className="rounded-3xl border border-white/10 bg-base-100/50 p-4">
                      <div className="text-sm font-semibold">3. Explore</div>
                      <div className="mt-1 text-xs text-base-content/65">Raid, loot, survive</div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 lg:col-span-5">
                  <button type="button" className="btn btn-primary btn-lg" onClick={copy}>
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    {copied ? 'Copied' : 'Copy server IP'}
                  </button>
                  <a href="#world" className="btn btn-ghost btn-lg border border-white/10">
                    View world map
                    <ArrowRight className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="py-16">
          <Container>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="Legal"
                  title="Privacy and terms"
                  description="Clear and simple policies for playing on the server."
                />
              </div>

              <div className="grid gap-4 lg:col-span-7">
                <div id="privacy" className="rounded-3xl border border-white/10 bg-base-200/20 p-6">
                  <div className="text-sm font-semibold">Privacy</div>
                  <p className="mt-3 text-sm leading-relaxed text-base-content/70">
                    We store the minimum data needed to run the server and keep it fair: your account name, unique
                    identifiers, connection logs, and anti-cheat telemetry. This data may be used for moderation, abuse
                    prevention, and troubleshooting. We do not sell player data.
                  </p>
                </div>

                <div id="terms" className="rounded-3xl border border-white/10 bg-base-200/20 p-6">
                  <div className="text-sm font-semibold">Terms</div>
                  <p className="mt-3 text-sm leading-relaxed text-base-content/70">
                    By connecting, you accept that gameplay is high-risk: PvP and raids are enabled and you can lose items
                    and bases. Cheating, exploit abuse, and attempts to bypass anti-cheat or moderation may result in
                    removal and loss of access. The server may change balance, crafting, and wipe schedules at any time.
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};
